import type { IResultstash, JsonQuery } from "@mne-core/api";
import { QueryController, createCsrfToken, createHeaders, fetchJsonQuery } from "@mne-core/api";

import AuthenticationURIBuilder from "./AuthenticationURIBuilder";

export interface IRequestRedirectURIOpenIDAuthorizationCriteria {
  operation: string;
  context?: { [key: string]: string };
  nextURI: string;
}

export interface IRequestRedirectURIOpenIDAuthorizationOpts extends RequestInit {
  apiUrl: string;
  apiKey?: string;
  sessionKey: string;
  ctrl?: QueryController;
}

export interface IRequestRedirectURIOpenIDAuthorizationResponse {
  providerURI: string; // The providerURI to redirect the user to
  stateID: string; // The state id to pass to the OpenID provider
}

export const createRequest = (
  body: IRequestRedirectURIOpenIDAuthorizationCriteria,
  opts: IRequestRedirectURIOpenIDAuthorizationOpts,
): Request => {
  const { apiUrl, apiKey, sessionKey, ...restOpts } = opts;
  const { nextURI, operation, context } = body;
  const uri = new AuthenticationURIBuilder().setApiUrl(apiUrl).setSegment(["openid", "authorize", "redirect"]);

  const headers = createHeaders({
    "ARGUS-API-KEY": apiKey,
    Accept: "application/json",
    "Content-type": "application/json",
    "ARGUS-CSRF-Token": !apiKey ? createCsrfToken(sessionKey, String(uri)) : false,
  });

  let localRequestOpts: RequestInit = {
    body: JSON.stringify({ nextURI, operation, context }),
    headers,
    method: "POST",
    mode: "cors",
  };

  if (!apiKey) {
    /**
     * Only add cookies if no apikey is provided
     */
    localRequestOpts = {
      ...localRequestOpts,
      credentials: "include",
    };
  }

  return new Request(String(uri), {
    ...localRequestOpts,
    ...restOpts,
  });
};

async function requestRedirectURIOpenIDAuthorization(
  body: IRequestRedirectURIOpenIDAuthorizationCriteria,
  opts: IRequestRedirectURIOpenIDAuthorizationOpts,
): Promise<JsonQuery<IResultstash<IRequestRedirectURIOpenIDAuthorizationResponse>>> {
  return await fetchJsonQuery<IResultstash<IRequestRedirectURIOpenIDAuthorizationResponse>>(createRequest(body, opts), {
    ctrl: opts.ctrl || new QueryController(),
  });
}

export default requestRedirectURIOpenIDAuthorization;
